import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";
import { riderSaleService } from "@/services/rider-sale";
import type { RiderSaleListParams } from "@/services/rider-sale";

import type {
  CreateRiderSaleReq,
  UpdateRiderSaleReq,
} from "@artisancode/api-types";

export function useRiderSales(params?: RiderSaleListParams) {
  return useQuery({
    queryKey: queryKeys.riderSales.list(params as Record<string, unknown>),
    queryFn: () => riderSaleService.list(params),
    enabled: params !== undefined,
  });
}

/** On-demand fetch of every rider sale matching params (all pages), e.g. for a PDF export. */
export function useFetchAllRiderSales() {
  const qc = useQueryClient();
  return (params: Omit<RiderSaleListParams, "page" | "per_page">) =>
    qc.fetchQuery({
      queryKey: [...queryKeys.riderSales.all, "all", params],
      queryFn: () => riderSaleService.listAll(params),
      staleTime: 0,
    });
}

export function useRiderSalePendingPaymentSummary(workspaceId?: string) {
  return useQuery({
    queryKey: [...queryKeys.riderSales.pendingPaymentSummary, workspaceId],
    queryFn: () => riderSaleService.pendingPaymentSummary(workspaceId),
  });
}

export function useCreateRiderSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      body,
      idempotencyKey,
    }: {
      body: CreateRiderSaleReq;
      idempotencyKey?: string;
    }) => riderSaleService.create(body, idempotencyKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.riderSales.all });
      qc.invalidateQueries({ queryKey: queryKeys.stocks.all });
    },
  });
}

export function useUpdateRiderSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & UpdateRiderSaleReq) =>
      riderSaleService.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.riderSales.all });
      qc.invalidateQueries({ queryKey: queryKeys.stocks.all });
    },
  });
}

export function useDeleteRiderSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      riderSaleService.delete(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.riderSales.all });
      qc.invalidateQueries({ queryKey: queryKeys.stocks.all });
    },
  });
}

export function useVoidRiderSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      riderSaleService.void(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.riderSales.all });
      qc.invalidateQueries({ queryKey: queryKeys.stocks.all });
    },
  });
}
